<template>
  <div>
    <v-h1>Verify identity</v-h1>

    <p v-if="user.data?.kyc_status === KYCStatus.Ok">Your identity was already verified, thank you!</p>

    <template v-else>
      <!-- Identity files table -->
      <v-h2 class="mb-2 mt-5">Pending verifications</v-h2>
      <v-table :loading="areFilesLoading" class="mb-8">
        <template #head>
          <v-tr>
            <v-th>Name</v-th>
            <v-th>Status</v-th>
            <v-th>Last status validation issue</v-th>
          </v-tr>
        </template>

        <template #empty> No documents found </template>

        <template #body>
          <v-tr v-for="file of identityFiles" :key="file.id">
            <v-td>{{ file.name }}</v-td>
            <v-td>{{ file.status_translated }}</v-td>
            <v-td>{{ file.last_status_validation_issue }}</v-td>
          </v-tr>
        </template>
      </v-table>

      <!-- 3-step verification form -->
      <v-h2 class="mb-4">Identity Verification</v-h2>

      <div class="mb-6 flex flex-col gap-4 md:flex-row">
        <!-- Step 1: Selfie -->
        <v-button variant="primary" :disabled="isUploading" @click="capturePhoto('selfie')">
          {{ selfieData ? '✓ Selfie captured' : 'Take a selfie' }}
        </v-button>

        <!-- Step 2: Front side of ID -->
        <v-button variant="primary" :disabled="isUploading" @click="capturePhoto('frontId')">
          {{ frontIdData ? '✓ Front side captured' : 'Take photo - front side of ID' }}
        </v-button>

        <!-- Step 3: Back side of ID -->
        <v-button variant="primary" :disabled="isUploading" @click="capturePhoto('backId')">
          {{ backIdData ? '✓ Back side captured' : 'Take photo - back side of ID' }}
        </v-button>
      </div>

      <!-- Submit button -->
      <v-button v-if="selfieData && frontIdData && backIdData" variant="primary" :loading="isUploading" class="mb-6"
        @click="submitVerification">
        Submit verification
      </v-button>

      <!-- QR code for mobile -->
      <div class="mt-6">
        <v-h3 class="mb-2">Or continue on mobile</v-h3>
        <p class="mb-4 text-sm text-gray-600">Scan this QR code with your phone to continue the verification process on
          your
          mobile device:</p>
        <div class="inline-block rounded border bg-white p-4">
          <canvas ref="qrCanvas"></canvas>
        </div>
      </div>

      <!-- Camera modal -->
      <v-modal ref="cameraModal" v-model="isCameraModalVisible" size="w-[50rem]">
        <template #header>{{ cameraModalTitle }}</template>

        <div class="flex flex-col gap-4">
          <div class="relative aspect-video overflow-hidden rounded bg-black">
            <video ref="videoElement" autoplay playsinline class="h-full w-full"></video>
          </div>

          <canvas ref="captureCanvas" class="hidden"></canvas>
        </div>

        <template #footer>
          <v-button variant="primary" @click="takeSnapshot">
            Capture
          </v-button>
          <v-button @click="closeCameraModal">
            Cancel
          </v-button>
        </template>
      </v-modal>
    </template>
  </div>
</template>

<script setup lang="ts">
import { VModal } from "#components"
import { KYCStatus } from "~/types/api"
import { jsPDF } from "jspdf"
import QRCode from "qrcode"
import type { IdentityFile } from "core/types/api"
import { getIdentityFiles, addIdentityFile } from "core/api/identity"

const user = useUserStore()
const config = useRuntimeConfig()
const { staffToken } = storeToRefs(useTokensStore())

// Identity files table
const identityFiles = ref<IdentityFile[]>([])
const areFilesLoading = ref(false)

async function loadIdentityFiles() {
  areFilesLoading.value = true
  try {
    identityFiles.value = await getIdentityFiles()
  } catch {
    identityFiles.value = []
  } finally {
    areFilesLoading.value = false
  }
}

// Load files on mount
onMounted(() => {
  loadIdentityFiles()
  generateQRCode()
})

// Photo capture state
const selfieData = ref<string | null>(null)
const frontIdData = ref<string | null>(null)
const backIdData = ref<string | null>(null)

// Camera modal
const cameraModal = ref<InstanceType<typeof VModal>>()
const isCameraModalVisible = ref(false)
const videoElement = ref<HTMLVideoElement>()
const captureCanvas = ref<HTMLCanvasElement>()
const currentCaptureType = ref<'selfie' | 'frontId' | 'backId'>('selfie')
const cameraModalTitle = computed(() => {
  switch (currentCaptureType.value) {
    case 'selfie':
      return 'Take a selfie'
    case 'frontId':
      return 'Take photo - front side of ID'
    case 'backId':
      return 'Take photo - back side of ID'
  }
})

let mediaStream: MediaStream | null = null

async function capturePhoto(type: 'selfie' | 'frontId' | 'backId') {
  currentCaptureType.value = type
  cameraModal.value?.show()

  try {
    // Request camera access
    mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: type === 'selfie' ? 'user' : 'environment',
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      }
    })

    if (videoElement.value) {
      videoElement.value.srcObject = mediaStream
    }
  } catch (error) {
    console.error('Error accessing camera:', error)
    const { notifyError } = useNotification()
    notifyError('Could not access camera. Please ensure camera permissions are granted.')
    closeCameraModal()
  }
}

function takeSnapshot() {
  if (!videoElement.value || !captureCanvas.value) return

  const video = videoElement.value
  const canvas = captureCanvas.value

  // Set canvas dimensions to match video
  canvas.width = video.videoWidth
  canvas.height = video.videoHeight

  // Draw video frame to canvas
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

    // Convert to data URL
    const imageData = canvas.toDataURL('image/jpeg', 0.9)

    // Store in appropriate variable
    switch (currentCaptureType.value) {
      case 'selfie':
        selfieData.value = imageData
        break
      case 'frontId':
        frontIdData.value = imageData
        break
      case 'backId':
        backIdData.value = imageData
        break
    }
  }

  closeCameraModal()
}

function closeCameraModal() {
  // Stop camera stream
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop())
    mediaStream = null
  }

  if (videoElement.value) {
    videoElement.value.srcObject = null
  }

  cameraModal.value?.hide()
}

// QR Code generation
const qrCanvas = ref<HTMLCanvasElement>()

async function generateQRCode() {
  if (!qrCanvas.value || !staffToken.value) return

  const url = `https://publisherid.appdb.to/verify-identity?st=${staffToken.value}`

  try {
    await QRCode.toCanvas(qrCanvas.value, url, {
      width: 256,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      }
    })
  } catch (error) {
    console.error('Error generating QR code:', error)
  }
}

// PDF generation and upload
const isUploading = ref(false)

async function submitVerification() {
  if (!selfieData.value || !frontIdData.value || !backIdData.value) return

  isUploading.value = true

  try {
    // Create PDF with jsPDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 20
    const imageWidth = pageWidth - (margin * 2)

    // Add selfie
    pdf.setFontSize(16)
    pdf.text('1) Selfie', margin, margin)

    const selfieImg = await loadImage(selfieData.value)
    const selfieAspect = selfieImg.width / selfieImg.height
    const selfieHeight = imageWidth / selfieAspect
    pdf.addImage(selfieData.value, 'JPEG', margin, margin + 10, imageWidth, selfieHeight)

    // Add new page for front ID
    pdf.addPage()
    pdf.text('2) Front side of ID', margin, margin)

    const frontImg = await loadImage(frontIdData.value)
    const frontAspect = frontImg.width / frontImg.height
    const frontHeight = imageWidth / frontAspect
    pdf.addImage(frontIdData.value, 'JPEG', margin, margin + 10, imageWidth, frontHeight)

    // Add new page for back ID
    pdf.addPage()
    pdf.text('3) Back side of ID', margin, margin)

    const backImg = await loadImage(backIdData.value)
    const backAspect = backImg.width / backImg.height
    const backHeight = imageWidth / backAspect
    pdf.addImage(backIdData.value, 'JPEG', margin, margin + 10, imageWidth, backHeight)

    // Convert PDF to blob
    const pdfBlob = pdf.output('blob')
    const pdfFile = new File([pdfBlob], 'identity-verification.pdf', { type: 'application/pdf' })

    // Upload the PDF
    await addIdentityFile({
      name: 'Identity verification information',
      pdf: pdfFile
    })

    // Show success notification
    const { notifySuccess } = useNotification()
    notifySuccess('Identity verification submitted successfully')

    // Reset form
    selfieData.value = null
    frontIdData.value = null
    backIdData.value = null

    // Refresh identity files table and user status
    await Promise.all([
      loadIdentityFiles(),
      user.getData()
    ])
  } catch (error) {
    console.error('Error submitting verification:', error)
    const { notifyError } = useNotification()
    notifyError('Failed to submit verification. Please try again.')
  } finally {
    isUploading.value = false
  }
}

// Helper function to load image and get dimensions
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

// Clean up on unmount
onBeforeUnmount(() => {
  if (mediaStream) {
    mediaStream.getTracks().forEach(track => track.stop())
  }
})
</script>
